// Student onboarding wizard — five-step professional flow.
// 1) Welcome (Deskey-branded, no AI emojis)
// 2) Pick areas of interest (with "Other" + free text)
// 3) Specific topics (universal grouping + custom topics)
// 4) About you (DOB picker → derived age band + favorite subjects + learning style)
// 5) Make it yours (theme + stickers, fits viewport, deep browser in dialog)
import React, { useEffect, useMemo, useState } from 'react';
import { clearStorageKeepLoginEndpoint, signedOutPath } from '@/lib/loginEndpoint';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from './studentApi';
import {
  STUDENT_THEMES, STICKER_PACKS, themesByTier, packsByTier,
  recommendedTier, THEME_TIERS, getTheme, themeToCssVars, TIER_STYLE,
  emojiToTwemojiUrl, recommendThemes, tagIcon,
} from './themes';
import {
  ChevronRight, ChevronLeft, Check, X, Calendar as CalendarIcon,
  GraduationCap, BookOpenText, Sparkles as VibeIcon, User as UserIcon,
} from 'lucide-react';

// ─── Constants ──────────────────────────────────────────────────────────────
const TOTAL_STEPS = 5;
const STEP_LABELS = ['Welcome', 'Interests', 'Topics', 'About you', 'Make it yours'];

// Habitual / lifestyle interests — these are NOT abstract study-skills,
// they're real-world passions that the MagicDesk AI uses as flavor hooks for
// every lesson example (e.g. "If you love football, momentum is like a
// striker breaking through a defender's tackle"). Backed by the affinity
// engine in `backend/student_affinity_routes.py` which maps each tag to a
// subject and learns from the student's interactions over time.
const UNIVERSAL_TAGS = [
  'football', 'cricket', 'basketball', 'gym & fitness', 'running',
  'music', 'singing', 'guitar', 'gaming', 'minecraft', 'esports',
  'movies', 'anime', 'k-drama', 'youtube', 'photography', 'sketching',
  'coding', 'robotics', 'space', 'cars', 'bikes', 'travel', 'food',
  'cooking', 'baking', 'pets', 'fashion', 'social media', 'memes',
  'reading', 'comics',
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const minDobIso = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 80); return d.toISOString().slice(0, 10); };

const MIN_STUDENT_AGE = 3;
const MAX_STUDENT_AGE = 120;

const computeAge = (iso) => {
  if (!iso) return null;
  try {
    const dob = new Date(iso);
    if (isNaN(dob.getTime())) return null;
    const now = new Date();
    if (dob > now) return null; // future DOB is never valid, regardless of arithmetic edge cases
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
    return age >= MIN_STUDENT_AGE && age < MAX_STUDENT_AGE ? age : null;
  } catch { return null; }
};

const ageToBand = (age) => {
  if (age == null) return null;
  if (age < 13) return 'under-13';
  if (age < 16) return '13-15';
  if (age < 19) return '16-18';
  return '18+';
};

// ─── Main ───────────────────────────────────────────────────────────────────
export default function StudentOnboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [opts, setOpts] = useState({ interest_categories: [], themes: [], stickers: [], learning_styles: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    interest_categories: [],
    interests: [],
    custom_interests: [],
    hobbies: [],
    learning_style: 'mixed',
    dob: '',
    age_band: null,
    theme_id: 'deskey-classic',
    stickers: [],
    favorite_subjects: [],
    content_genres: [],
    font_scale: 'md',
    font_override: '',
  });
  const [favSubjInput, setFavSubjInput] = useState('');
  const [otherTagInput, setOtherTagInput] = useState('');
  const [subjectOptions, setSubjectOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get('/student/onboarding/options').then(d => {
      if (!cancelled) { setOpts(d); setLoading(false); }
    }).catch(e => { console.error(e); setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // The admin records DOB when adding the student — reuse it instead of
    // asking again. `_dob_from_admin` hides the DOB field on step 3; the
    // `_` prefix keeps it out of the save payload.
    api.get('/student/me').then(d => {
      const adminDob = (d?.user?.dob || '').slice(0, 10);
      if (!cancelled && computeAge(adminDob) != null) {
        setData(prev => ({ ...prev, dob: adminDob, _dob_from_admin: true }));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Best-effort: independent students with no tenant yet just get an
    // empty list here and fall back to the free-text "Add" button below.
    api.get('/subjects').then(d => {
      if (!cancelled) setSubjectOptions(Array.isArray(d) ? d : []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Apply student's chosen theme as the wizard surface so step 5 previews live
  const theme = useMemo(() => getTheme(data.theme_id), [data.theme_id]);
  const cssVars = themeToCssVars(theme);

  // Helpers
  const toggle = (key, val) => {
    setData(d => {
      const arr = d[key] || [];
      return { ...d, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
    });
  };

  const addToList = (key, value) => {
    const v = (value || '').trim();
    if (!v) return;
    setData(d => (d[key] || []).includes(v) ? d : { ...d, [key]: [...(d[key] || []), v] });
  };

  const removeFromList = (key, value) =>
    setData(d => ({ ...d, [key]: (d[key] || []).filter(x => x !== value) }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(data).filter(([k]) => !k.startsWith('_')));
      // age_band is computed server-side from dob; send dob too for traceability
      payload.age_band = ageToBand(computeAge(data.dob));
      await api.post('/student/onboarding/save', payload);
      toast.success('Profile saved. Welcome to Deskey.');
      nav('/student/desk');
    } catch (e) {
      toast.error(e.message || 'Failed to save profile');
    } finally { setSaving(false); }
  };

  // Step gates: each step has a soft "complete enough to proceed" rule
  const stepValid = useMemo(() => {
    if (step === 1) return data.interest_categories.length >= 1 || data.custom_interests.length >= 1;
    if (step === 2) return data.interests.length >= 1 || data.custom_interests.length >= 1;
    if (step === 3) return Boolean(data.dob) && computeAge(data.dob) != null;
    return true;
  }, [step, data]);

  const next = () => setStep(s => Math.min(TOTAL_STEPS - 1, s + 1));
  const prev = () => setStep(s => Math.max(0, s - 1));

  // All tags merged: from picked categories + universal pool + custom + existing tags from all categories
  const tagsForStep2 = useMemo(() => {
    const picked = data.interest_categories;
    const fromPicked = (opts.interest_categories || [])
      .filter(c => picked.length === 0 || picked.includes(c.id))
      .flatMap(c => c.tags.map(t => ({ tag: t, cat: c.label, group: c.label, emoji: c.emoji })));
    const universal = UNIVERSAL_TAGS.map(t => ({ tag: t, cat: 'Everyday loves', group: 'Everyday loves', emoji: '💫' }));
    // Deduplicate by tag, preserving first occurrence
    const seen = new Set();
    return [...fromPicked, ...universal].filter(o => {
      const k = o.tag.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k); return true;
    });
  }, [opts, data.interest_categories]);

  // Group step-2 tags by category for clean rendering
  const tagsGrouped = useMemo(() => {
    const m = new Map();
    for (const t of tagsForStep2) {
      if (!m.has(t.group)) m.set(t.group, { emoji: t.emoji, items: [] });
      m.get(t.group).items.push(t);
    }
    return [...m.entries()].map(([group, v]) => [group, v.emoji, v.items]);
  }, [tagsForStep2]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm text-gray-400">Loading…</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full overflow-x-hidden flex flex-col"
      style={{ ...cssVars, background: 'var(--st-bg)', color: 'var(--st-ink)', fontFamily: 'var(--st-font-body)' }}
      data-testid="student-onboarding">

      {/* Header progress (Deskey-branded) */}
      <header className="sticky top-0 z-30 backdrop-blur border-b" style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }}>
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white font-bold text-sm" style={{ background: 'var(--st-primary)' }}>D</div>
            <div className="leading-tight">
              <div className="font-extrabold text-sm tracking-tight" style={{ fontFamily: 'var(--st-font-heading)' }}>MyDeskey</div>
              <div className="text-[10px] opacity-60 -mt-0.5">Student onboarding</div>
            </div>
          </div>
          <div className="flex-1 flex items-center gap-1.5">
            {STEP_LABELS.map((label, i) => (
              <div key={i} className="flex-1">
                <div
                  className="h-[3px] rounded-full transition-all"
                  style={{ background: i <= step ? 'var(--st-primary)' : 'var(--st-border)' }} />
                <div className="text-[10px] mt-1 truncate hidden sm:block" style={{ color: i === step ? 'var(--st-ink)' : 'var(--st-mute)', fontWeight: i === step ? 700 : 500 }}>
                  {String(i + 1).padStart(2, '0')} · {label}
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => { clearStorageKeepLoginEndpoint(); nav(signedOutPath()); }} className="text-xs hover:underline whitespace-nowrap" style={{ color: 'var(--st-mute)' }} data-testid="onboarding-logout">Sign out</button>
        </div>
      </header>

      <main className={`flex-1 w-full mx-auto px-6 ${step === 4 ? 'max-w-6xl py-5 pb-24' : 'max-w-3xl py-10 sm:py-14 pb-32'}`}>
        {step === 0 && <StepWelcome onContinue={next} />}

        {step === 1 && (
          <StepInterests
            opts={opts}
            data={data}
            toggle={toggle}
          />
        )}

        {step === 2 && (
          <StepTopics
            tagsGrouped={tagsGrouped}
            data={data}
            toggle={toggle}
            otherTagInput={otherTagInput}
            setOtherTagInput={setOtherTagInput}
            addCustom={(v) => addToList('custom_interests', v)}
            removeCustom={(v) => removeFromList('custom_interests', v)}
          />
        )}

        {step === 3 && (
          <StepAbout
            opts={opts}
            data={data}
            setData={setData}
            subjectOptions={subjectOptions}
            favSubjInput={favSubjInput} setFavSubjInput={setFavSubjInput}
            addToList={addToList}
            removeFromList={removeFromList}
          />
        )}

        {step === 4 && (
          <StepMakeItYours
            data={data}
            setData={setData}
            toggle={toggle}
          />
        )}
      </main>

      {/* Footer Nav */}
      <footer className="fixed bottom-0 left-0 right-0 backdrop-blur border-t z-40" style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }}>
        <div className="max-w-3xl mx-auto px-6 py-3.5 flex items-center justify-between gap-3">
          <Button onClick={prev} disabled={step === 0} variant="ghost"
            className="rounded-full px-4 disabled:opacity-30"
            data-testid="onboard-prev"
            style={{ color: 'var(--st-ink)' }}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="text-[11px] font-semibold" style={{ color: 'var(--st-mute)' }}>
            Step {step + 1} of {TOTAL_STEPS}
          </div>
          {step < TOTAL_STEPS - 1 ? (
            <Button onClick={next} disabled={!stepValid}
              className="rounded-full px-5 text-white font-semibold disabled:opacity-40"
              style={{ background: 'var(--st-primary)' }}
              data-testid="onboard-next">
              Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving}
              className="rounded-full px-5 text-white font-semibold"
              style={{ background: 'var(--st-primary)' }}
              data-testid="onboard-finish">
              {saving ? 'Saving…' : 'Enter Deskey'}
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 0 — Welcome
// ═══════════════════════════════════════════════════════════════════════════
function StepWelcome({ onContinue }) {
  return (
    <section className="text-left max-w-2xl" data-testid="onboarding-step-welcome">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border text-[11px] font-semibold uppercase tracking-widest mb-7"
        style={{ borderColor: 'var(--st-border)', color: 'var(--st-mute)', background: 'var(--st-card)' }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--st-primary)' }} />
        Welcome
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.04]" style={{ fontFamily: 'var(--st-font-heading)' }}>
        Let&rsquo;s set up your<br />
        <span style={{ color: 'var(--st-primary)' }}>Deskey workspace.</span>
      </h1>
      <p className="text-base sm:text-lg mt-6 leading-relaxed max-w-xl" style={{ color: 'var(--st-mute)' }}>
        A short five-step setup so every lesson, recommendation and dashboard moment
        is tailored to how you study. You can change anything later from Settings.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10">
        {[
          { icon: BookOpenText,  label: 'Your interests',     desc: 'Personalise topics & examples' },
          { icon: GraduationCap, label: 'Your study profile', desc: 'Age, subjects, learning style' },
          { icon: VibeIcon,      label: 'Your workspace',     desc: 'Theme, typography, stickers' },
        ].map(({ icon: Icon, label, desc }) => (
          <div key={label}
            className="rounded-2xl px-4 py-4 border"
            style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)' }}>
              <Icon className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
            </div>
            <div className="text-sm font-bold" style={{ fontFamily: 'var(--st-font-heading)' }}>{label}</div>
            <div className="text-[12px] mt-1" style={{ color: 'var(--st-mute)' }}>{desc}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex items-center gap-3">
        <Button onClick={onContinue}
          className="rounded-full px-6 text-white font-semibold"
          style={{ background: 'var(--st-primary)' }}
          data-testid="welcome-continue">
          Begin setup <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
        <span className="text-[11px]" style={{ color: 'var(--st-mute)' }}>Takes about 2 minutes</span>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 1 — Interests (with "Other")
// ═══════════════════════════════════════════════════════════════════════════
function StepInterests({ opts, data, toggle }) {
  return (
    <section data-testid="onboarding-step-categories">
      <SectionHeader
        title="Pick your areas of interest"
        subtitle="Choose two or more — we use these to keep examples and recommendations relevant. Don't see one? Add your own."
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 mt-7">
        {opts.interest_categories.map(c => {
          const active = data.interest_categories.includes(c.id);
          const tw = emojiToTwemojiUrl(c.emoji);
          return (
            <button key={c.id} onClick={() => toggle('interest_categories', c.id)}
              data-testid={`onboard-cat-${c.id}`}
              className="relative rounded-2xl p-3 text-left border-2 transition-all overflow-hidden"
              style={{
                borderColor: active ? 'var(--st-primary)' : 'var(--st-border)',
                background: active ? 'var(--st-primary)' : 'var(--st-card)',
                color: active ? '#fff' : 'var(--st-ink)',
                transform: active ? 'translateY(-1px)' : 'none',
              }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2.5"
                style={{ background: active ? 'rgba(255,255,255,0.18)' : 'var(--st-bg)', border: active ? 'none' : '1px solid var(--st-border)' }}>
                {tw
                  ? <img src={tw} alt="" className="w-6 h-6 object-contain" data-testid={`onboard-cat-icon-${c.id}`} />
                  : <span className="text-xl">{c.emoji}</span>}
              </div>
              <div className="text-sm font-bold" style={{ fontFamily: 'var(--st-font-heading)' }}>{c.label}</div>
              <div className="text-[10px] mt-1 leading-tight opacity-70 line-clamp-2">{c.tags.slice(0, 4).join(' · ')}</div>
              {active && <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white text-[var(--st-primary)] flex items-center justify-center"><Check className="w-3 h-3" /></div>}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 2 — Topics (universal grouping + free text)
// ═══════════════════════════════════════════════════════════════════════════
function StepTopics({ tagsGrouped, data, toggle, otherTagInput, setOtherTagInput, addCustom, removeCustom }) {
  return (
    <section data-testid="onboarding-step-tags">
      <SectionHeader
        title="What do you enjoy most?"
        subtitle="Pick the everyday things you love — football, music, gaming, cooking, anime. Our AI weaves these into every Magic Desk lesson, so physics, math and biology examples land in YOUR world. The more you tap, the smarter it gets."
      />

      <div className="space-y-5 mt-7">
        {tagsGrouped.map(([group, groupEmoji, tags]) => {
          const tw = emojiToTwemojiUrl(groupEmoji);
          return (
            <div key={group}>
              <div className="flex items-center gap-2 mb-2">
                {tw
                  ? <img src={tw} alt="" className="w-4 h-4 object-contain" data-testid={`onboard-group-icon-${group.toLowerCase().replace(/\s+/g, '-')}`} />
                  : <span className="text-base leading-none">{groupEmoji}</span>}
                <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--st-mute)' }}>{group}</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {tags.map(t => {
                  const active = data.interests.includes(t.tag);
                  const perTag = tagIcon(t.tag, t.emoji);
                  const ttw = emojiToTwemojiUrl(perTag);
                  const tagSlug = t.tag.replace(/\s+/g, '-');
                  return (
                    <button key={t.tag} onClick={() => toggle('interests', t.tag)}
                      data-testid={`onboard-tag-${tagSlug}`}
                      className="px-2.5 py-1.5 rounded-md text-[12px] font-semibold border transition-all inline-flex items-center gap-1.5"
                      style={{
                        borderColor: active ? 'var(--st-primary)' : 'var(--st-border)',
                        background: active ? 'var(--st-primary)' : 'var(--st-card)',
                        color: active ? '#fff' : 'var(--st-ink)',
                      }}>
                      {ttw
                        ? <img src={ttw} alt="" className="w-3.5 h-3.5 object-contain opacity-95" data-testid={`onboard-tag-icon-${tagSlug}`} />
                        : <span className="text-[14px] leading-none">{perTag}</span>}
                      {t.tag}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Free-text "Other" */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--st-mute)' }}>Add your own</div>
          <div className="flex gap-2">
            <Input value={otherTagInput}
              onChange={e => setOtherTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (otherTagInput.trim()) { addCustom(otherTagInput); setOtherTagInput(''); } } }}
              placeholder="Type a topic you love and press Enter"
              className="rounded-lg"
              data-testid="onboard-tag-other-input"
              style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)', color: 'var(--st-ink)' }} />
            <Button onClick={() => { if (otherTagInput.trim()) { addCustom(otherTagInput); setOtherTagInput(''); } }}
              className="rounded-lg text-white font-semibold"
              style={{ background: 'var(--st-primary)' }}
              data-testid="onboard-tag-other-add">Add</Button>
          </div>
          {data.custom_interests?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {data.custom_interests.map(v => (
                <span key={v} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-xs font-semibold"
                  style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
                  {v}
                  <button onClick={() => removeCustom(v)} className="opacity-50 hover:opacity-100">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 3 — About you (DOB + favorite subjects + hobbies + learning style)
// ═══════════════════════════════════════════════════════════════════════════
function StepAbout({ opts, data, setData, subjectOptions, favSubjInput, setFavSubjInput, addToList, removeFromList }) {
  const age = computeAge(data.dob);
  const band = ageToBand(age);
  const dobInvalid = Boolean(data.dob) && age == null;
  return (
    <section data-testid="onboarding-step-about" className="space-y-8">
      <SectionHeader
        title="A little about you"
        subtitle="Helps us pitch lessons at the right level and pick a starter look that suits you."
      />

      {/* Date of Birth — skipped when the admin already recorded it */}
      {!data._dob_from_admin && (
      <FieldGroup label="Date of birth" hint="We use this to match content difficulty. Never shared.">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <Input type="date"
              value={data.dob || ''}
              max={todayIso()}
              min={minDobIso()}
              onChange={e => setData(d => ({ ...d, dob: e.target.value }))}
              className="rounded-lg pl-9 w-[210px]"
              data-testid="onboard-dob-input"
              style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)', color: 'var(--st-ink)' }} />
          </div>
          {age != null && (
            <div className="text-xs px-3 py-1.5 rounded-md border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid="onboard-dob-derived">
              <span className="opacity-60">Age:</span> <b>{age}</b>
              <span className="opacity-60 ml-2">·</span>
              <span className="opacity-60 ml-2">Band:</span> <b className="capitalize">{band}</b>
            </div>
          )}
        </div>
        {dobInvalid && (
          <p className="text-xs mt-2 text-red-500" data-testid="onboard-dob-error">
            That date isn&rsquo;t valid.
          </p>
        )}
      </FieldGroup>
      )}

      {/* Favorite subjects */}
      <FieldGroup label="Favorite subjects" hint="Pick from your school's list or type your own">
        <div className="flex gap-2">
          <Input value={favSubjInput} onChange={e => setFavSubjInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addToList('favorite_subjects', favSubjInput); setFavSubjInput(''); } }}
            placeholder="Mathematics · Biology · Literature"
            className="rounded-lg"
            list={subjectOptions.length > 0 ? 'onboard-subject-options' : undefined}
            data-testid="onboard-favsubj-input"
            style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)', color: 'var(--st-ink)' }} />
          <Button onClick={() => { addToList('favorite_subjects', favSubjInput); setFavSubjInput(''); }}
            className="rounded-lg text-white font-semibold" style={{ background: 'var(--st-primary)' }} data-testid="onboard-favsubj-add">Add</Button>
        </div>
        {subjectOptions.length > 0 && (
          <datalist id="onboard-subject-options">
            {subjectOptions.map(s => <option key={s.id} value={s.name} />)}
          </datalist>
        )}
        <ChipList items={data.favorite_subjects} onRemove={(v) => removeFromList('favorite_subjects', v)} />
      </FieldGroup>

      {/* Learning style */}
      <FieldGroup label="How you learn best" hint="Pick the one that fits you most">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {opts.learning_styles.map(s => {
            const active = data.learning_style === s.id;
            return (
              <button key={s.id} onClick={() => setData(d => ({ ...d, learning_style: s.id }))}
                data-testid={`onboard-style-${s.id}`}
                className="text-left rounded-xl p-3 border transition-all"
                style={{
                  borderColor: active ? 'var(--st-primary)' : 'var(--st-border)',
                  background: active ? 'var(--st-primary)' : 'var(--st-card)',
                  color: active ? '#fff' : 'var(--st-ink)',
                  borderWidth: active ? 2 : 1,
                }}>
                <div className="text-sm font-bold" style={{ fontFamily: 'var(--st-font-heading)' }}>{s.label}</div>
                <div className="text-[11px] opacity-75 mt-1 leading-snug">{s.desc}</div>
              </button>
            );
          })}
        </div>
      </FieldGroup>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 4 — Make it yours (compact fit-to-screen layout)
//   Two columns on >= md:
//   ▸ Left  : Standard Deskey row + Recommended row (vertical stack)
//   ▸ Right : Live preview + Browse-all triggers
// ═══════════════════════════════════════════════════════════════════════════
function StepMakeItYours({ data, setData, toggle }) {
  const recoTier = data._theme_tier || recommendedTier(ageToBand(computeAge(data.dob)));
  const activeTheme = STUDENT_THEMES[data.theme_id];
  const signals = {
    interests: data.interests,
    custom_interests: data.custom_interests,
    hobbies: data.hobbies,
    favorite_subjects: data.favorite_subjects,
    age_band: ageToBand(computeAge(data.dob)),
    learning_style: data.learning_style,
  };
  return (
    <section data-testid="onboarding-step-theme" className="space-y-4">
      <SectionHeader
        title="Make it yours"
        subtitle="Pick a Deskey standard, switch to a curated recommendation, or browse every theme — preview lives on the right."
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left column — recommendations stack */}
        <div className="lg:col-span-3 space-y-3">
          <StandardDeskeyRow
            value={data.theme_id}
            onPick={(id) => setData(d => ({ ...d, theme_id: id, _theme_tier: STUDENT_THEMES[id]?.tier || d._theme_tier }))}
          />
          <RecommendedThemesRow
            signals={signals}
            value={data.theme_id}
            onPick={(id) => setData(d => ({ ...d, theme_id: id, _theme_tier: STUDENT_THEMES[id]?.tier || d._theme_tier }))}
          />
        </div>

        {/* Right column — live preview + popup triggers */}
        <div className="lg:col-span-2 space-y-3">
          <LivePreviewStrip
            theme={activeTheme}
            accent={data.accent_color}
            onAccent={(c) => setData(d => ({ ...d, accent_color: c }))}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
            <ThemePopupTrigger
              activeTheme={activeTheme}
              tier={recoTier}
              onPick={(id) => setData(d => ({ ...d, theme_id: id, _theme_tier: STUDENT_THEMES[id]?.tier || d._theme_tier }))}
              onTier={(t) => setData(d => ({ ...d, _theme_tier: t }))}
            />
            <StickerPopupTrigger
              selected={data.stickers}
              onToggle={(id) => toggle('stickers', id)}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// "Standard Deskey" — six universal defaults shown to every learner regardless of age
const STANDARD_DESKEY_IDS = ['deskey-classic', 'slate-pro', 'scholarly-elite', 'pearl-noir', 'platinum-edge', 'horizon-pro'];

function StandardDeskeyRow({ value, onPick }) {
  const picks = STANDARD_DESKEY_IDS.map(id => STUDENT_THEMES[id]).filter(Boolean);
  return (
    <div className="rounded-2xl border p-4" data-testid="onboard-standard-row"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--st-primary)' }}>Standard Deskey</div>
          <div className="text-[11px] mt-0.5" style={{ color: 'var(--st-mute)' }}>Universally pro · same look across every age</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {picks.map(th => <ThemePreviewCard key={th.id} th={th} active={value === th.id} onClick={() => onPick(th.id)} testid={`onboard-standard-${th.id}`} />)}
      </div>
    </div>
  );
}

// ── Live preview strip with sample heading + button + accent picker ─────────
const ACCENT_SWATCHES = [
  null,        '#2563EB', '#0EA5E9', '#14B8A6', '#22C55E',
  '#EAB308',   '#F97316', '#EF4444', '#EC4899', '#A855F7',
  '#6366F1',   '#0F172A',
];

function LivePreviewStrip({ theme, accent, onAccent }) {
  if (!theme) return null;
  const primary = accent || theme.primary;
  const tierStyle = TIER_STYLE[theme.tier] || TIER_STYLE.standard;
  return (
    <div className="rounded-2xl border overflow-hidden" data-testid="theme-live-preview"
      style={{ borderColor: 'var(--st-border)' }}>
      <div className="px-5 py-4"
        style={{
          background: theme.bg,
          color: theme.ink,
          borderBottom: `1px solid ${theme.border}`,
          fontFamily: `"${theme.bodyFont}", ui-sans-serif`,
        }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: theme.primary }}>Preview · {theme.tier}</div>
            <h3 className="text-xl font-extrabold mt-1" style={{ fontFamily: `"${theme.headingFont}", ui-serif`, color: theme.ink }}>{theme.name}</h3>
            <p className="text-[12px] mt-1.5 max-w-md leading-relaxed" style={{ color: theme.ink, opacity: 0.85 }}>This is how your desk will feel. Buttons, headings, and paragraphs all take on the active theme's fonts and rhythm.</p>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: theme.ink, opacity: 0.55 }}>Sample button</div>
            <button
              type="button"
              tabIndex={-1}
              aria-label="Open Desk button preview"
              data-testid="theme-live-preview-button"
              style={{
                background: primary,
                color: '#fff',
                borderRadius: tierStyle.btnRadius,
                letterSpacing: tierStyle.letterSpacing,
                padding: `${tierStyle.btnPaddingY} ${tierStyle.btnPaddingX}`,
                fontWeight: 600,
                fontSize: '12px',
                fontFamily: `"${theme.bodyFont}", ui-sans-serif`,
              }}>
              Open Desk
            </button>
          </div>
        </div>
      </div>

      {/* Accent color row */}
      <div className="px-5 py-3 flex items-center gap-3 flex-wrap" style={{ background: 'var(--st-card)' }}>
        <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--st-mute)' }}>Custom accent</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {ACCENT_SWATCHES.map((c, i) => {
            const active = (c || null) === (accent || null);
            return (
              <button key={i} onClick={() => onAccent(c)}
                data-testid={`theme-accent-${c || 'theme'}`}
                title={c ? `Accent ${c}` : `Use theme default (${theme.primary})`}
                className="w-6 h-6 rounded-full border-2 transition-all"
                style={{
                  background: c || theme.primary,
                  borderColor: active ? 'var(--st-ink)' : 'var(--st-border)',
                  transform: active ? 'scale(1.12)' : 'none',
                  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.15)' : 'none',
                }}>
                {!c && active ? <span className="block w-1.5 h-1.5 rounded-full bg-white mx-auto" /> : null}
              </button>
            );
          })}
          <label className="ml-1 text-[11px] cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded-md border"
            style={{ borderColor: 'var(--st-border)', color: 'var(--st-ink)' }} data-testid="theme-accent-picker-label">
            <input type="color" value={accent || theme.primary} onChange={e => onAccent(e.target.value)}
              className="w-4 h-4 rounded-sm cursor-pointer" data-testid="theme-accent-picker"
              style={{ background: 'transparent', border: 'none' }} />
            <span>Custom</span>
          </label>
          {accent && (
            <button onClick={() => onAccent(null)}
              data-testid="theme-accent-reset"
              className="text-[11px] opacity-60 hover:opacity-100 underline ml-1">Reset</button>
          )}
        </div>
      </div>
    </div>
  );
}

// Shared theme card used by Standard row, Recommended row, and Popup grid.
// Important: text uses the THEME's ink color (no opacity tricks) so Scholarly Elite
// and Standard/University themes always render clearly on their own backgrounds.
function ThemePreviewCard({ th, active, onClick, testid, compact = false }) {
  return (
    <button onClick={onClick}
      data-testid={testid || `onboard-theme-${th.id}`}
      className={`relative rounded-xl ${compact ? 'p-2.5' : 'p-3'} border-2 text-left transition-all overflow-hidden`}
      style={{
        borderColor: active ? th.primary : th.border,
        background: th.bg,
        color: th.ink,
        fontFamily: `"${th.headingFont}", ui-serif`,
        transform: active ? 'translateY(-2px)' : 'none',
      }}>
      <div className="flex gap-1 mb-2">
        <span className="w-4 h-4 rounded" style={{ background: th.primary }} />
        <span className="w-4 h-4 rounded" style={{ background: th.accent }} />
        <span className="w-4 h-4 rounded" style={{ background: th.card, border: `1px solid ${th.border}` }} />
      </div>
      <div className="text-[12px] font-bold leading-tight truncate" style={{ color: th.ink }}>{th.name}</div>
      <div className="text-[10px] capitalize truncate" style={{ color: th.ink, opacity: 0.7 }}>{th.tier} · {th.headingFont}</div>
      {active && <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full text-white flex items-center justify-center" style={{ background: th.primary }}><Check className="w-2.5 h-2.5" /></div>}
    </button>
  );
}

// ── Theme popup trigger: a compact summary card that opens the full browser in a Dialog ──
// Dialog chrome uses a FIXED neutral palette so it stays readable on every theme
// (including very dark Heroic / Cosmic / Stealth tiers). Theme PREVIEW cards inside
// the grid still use each theme's own colors so users see their true look.
const POPUP_SURFACE = {
  bg:      '#FFFFFF',
  ink:     '#0F172A',
  mute:    '#475569',
  border:  '#E2E8F0',
  subtle:  '#F8FAFC',
  primary: '#2563EB',
};

function ThemePopupTrigger({ activeTheme, tier, onPick, onTier }) {
  const [open, setOpen] = useState(false);
  const [activeTier, setActiveTier] = useState(tier || 'standard');
  useEffect(() => { setActiveTier(tier || 'standard'); }, [tier]);
  // Special "all" pseudo-tier shows every theme regardless of age band
  const themesToShow = activeTier === 'all'
    ? Object.values(STUDENT_THEMES)
    : themesByTier(activeTier);
  const totalThemes = Object.keys(STUDENT_THEMES).length;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="rounded-2xl border p-4 text-left transition-all hover:-translate-y-0.5"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
          data-testid="theme-browser-open">
          <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--st-mute)' }}>Every theme</div>
          <div className="flex items-center gap-3 mt-3">
            <div className="flex gap-1">
              <span className="w-7 h-7 rounded-md" style={{ background: activeTheme?.primary }} />
              <span className="w-7 h-7 rounded-md" style={{ background: activeTheme?.accent }} />
              <span className="w-7 h-7 rounded-md" style={{ background: activeTheme?.card, border: `1px solid ${activeTheme?.border}` }} />
            </div>
            <div className="min-w-0">
              <div className="text-base font-bold truncate" style={{ fontFamily: 'var(--st-font-heading)' }}>{activeTheme?.name || 'Choose a theme'}</div>
              <div className="text-[11px] capitalize" style={{ color: 'var(--st-mute)' }}>{activeTheme?.tier} · {activeTheme?.headingFont}</div>
            </div>
          </div>
          <div className="text-[11px] mt-3" style={{ color: 'var(--st-primary)', fontWeight: 600 }}>Browse all {totalThemes} themes →</div>
        </button>
      </DialogTrigger>
      <DialogContent
        className="max-w-3xl max-h-[82vh] overflow-hidden p-0"
        style={{ background: POPUP_SURFACE.bg, color: POPUP_SURFACE.ink, borderColor: POPUP_SURFACE.border }}
        data-testid="theme-browser-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b" style={{ borderColor: POPUP_SURFACE.border }}>
          <DialogTitle style={{ color: POPUP_SURFACE.ink, fontFamily: 'Inter, ui-sans-serif' }}>Choose your theme</DialogTitle>
          <DialogDescription style={{ color: POPUP_SURFACE.mute }}>{totalThemes} curated palettes & font pairings across {THEME_TIERS.length} tiers — try any of them.</DialogDescription>
        </DialogHeader>
        <div className="px-6 pt-4">
          <div className="flex flex-wrap gap-1 p-1 rounded-xl border" style={{ borderColor: POPUP_SURFACE.border, background: POPUP_SURFACE.subtle }} role="tablist">
            <button onClick={() => setActiveTier('all')}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all whitespace-nowrap"
              data-testid="onboard-tier-all"
              style={{ background: activeTier === 'all' ? POPUP_SURFACE.primary : 'transparent', color: activeTier === 'all' ? '#fff' : POPUP_SURFACE.ink }}>
              All <span className="ml-1.5 opacity-60 font-normal">{totalThemes}</span>
            </button>
            {THEME_TIERS.map(tt => {
              const a = activeTier === tt.id;
              return (
                <button key={tt.id} onClick={() => { setActiveTier(tt.id); onTier && onTier(tt.id); }}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all whitespace-nowrap"
                  data-testid={`onboard-tier-${tt.id}`}
                  style={{ background: a ? POPUP_SURFACE.primary : 'transparent', color: a ? '#fff' : POPUP_SURFACE.ink }}>
                  {tt.label}
                  <span className="ml-1.5 opacity-60 font-normal">{themesByTier(tt.id).length}</span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] mt-2" style={{ color: POPUP_SURFACE.mute }}>
            {activeTier === 'all' ? 'Showing every theme regardless of age band.' : THEME_TIERS.find(x => x.id === activeTier)?.desc}
          </p>
        </div>
        <div className="px-6 py-4 overflow-auto" style={{ maxHeight: 'calc(82vh - 200px)' }}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {themesToShow.map(th => (
              <ThemePreviewCard
                key={th.id}
                th={th}
                active={activeTheme?.id === th.id}
                onClick={() => onPick(th.id)}
              />
            ))}
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end" style={{ borderColor: POPUP_SURFACE.border, background: POPUP_SURFACE.subtle }}>
          <Button onClick={() => setOpen(false)}
            className="rounded-full text-white font-semibold px-5"
            style={{ background: POPUP_SURFACE.primary }} data-testid="theme-browser-done">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Sticker popup trigger: compact card → dialog with all packs ────────────
function StickerPopupTrigger({ selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const [pack, setPack] = useState('smileys');
  const activePack = STICKER_PACKS.find(p => p.id === pack) || STICKER_PACKS[0];
  const tierGroups = ['universal', 'mature', 'playful'];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="rounded-2xl border p-4 text-left transition-all hover:-translate-y-0.5"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
          data-testid="sticker-browser-open">
          <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--st-mute)' }}>Stickers</div>
          <div className="flex items-center gap-2 mt-3 min-h-[44px]">
            {selected.length === 0 ? (
              <span className="text-sm" style={{ color: 'var(--st-mute)' }}>None selected yet</span>
            ) : (
              selected.slice(0, 6).map(id => {
                const s = STICKER_PACKS.flatMap(p => p.stickers).find(x => x.id === id);
                if (!s) return null;
                const tw = emojiToTwemojiUrl(s.emoji);
                return tw
                  ? <img key={id} src={tw} alt="" className="w-7 h-7 object-contain" />
                  : <span key={id} className="text-xl">{s.emoji}</span>;
              })
            )}
            {selected.length > 6 && (
              <span className="text-[11px] font-bold ml-1" style={{ color: 'var(--st-mute)' }}>+{selected.length - 6}</span>
            )}
          </div>
          <div className="text-[11px] mt-3" style={{ color: 'var(--st-primary)', fontWeight: 600 }}>Choose from 20+ packs →</div>
        </button>
      </DialogTrigger>
      <DialogContent
        className="max-w-3xl max-h-[82vh] overflow-hidden p-0"
        style={{ background: POPUP_SURFACE.bg, color: POPUP_SURFACE.ink, borderColor: POPUP_SURFACE.border }}
        data-testid="sticker-browser-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b" style={{ borderColor: POPUP_SURFACE.border }}>
          <DialogTitle style={{ color: POPUP_SURFACE.ink, fontFamily: 'Inter, ui-sans-serif' }}>Choose your stickers</DialogTitle>
          <DialogDescription style={{ color: POPUP_SURFACE.mute }}>{selected.length} selected — drag &amp; place them on the desk later.</DialogDescription>
        </DialogHeader>
        <div className="px-6 pt-4 space-y-2">
          {tierGroups.map(t => {
            const packs = packsByTier(t);
            if (!packs.length) return null;
            return (
              <div key={t}>
                <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: POPUP_SURFACE.mute }}>{t}</div>
                <div className="flex flex-wrap gap-1">
                  {packs.map(p => {
                    const a = pack === p.id;
                    return (
                      <button key={p.id} onClick={() => setPack(p.id)}
                        data-testid={`onboard-pack-${p.id}`}
                        className="px-2.5 py-1 rounded-md text-[11px] font-bold border transition-all"
                        style={{ borderColor: a ? POPUP_SURFACE.primary : POPUP_SURFACE.border, background: a ? POPUP_SURFACE.primary : POPUP_SURFACE.subtle, color: a ? '#fff' : POPUP_SURFACE.ink }}>
                        {p.label} <span className="opacity-60 font-normal">{p.stickers.length}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-6 py-4 overflow-auto" style={{ maxHeight: 'calc(82vh - 240px)' }}>
          <div className="flex flex-wrap gap-2">
            {activePack.stickers.map(s => {
              const active = selected.includes(s.id);
              const tw = emojiToTwemojiUrl(s.emoji);
              return (
                <button key={s.id} onClick={() => onToggle(s.id)} title={s.label}
                  data-testid={`onboard-sticker-${s.id}`}
                  className="w-12 h-12 rounded-xl flex items-center justify-center border-2 transition-all"
                  style={{
                    borderColor: active ? POPUP_SURFACE.primary : POPUP_SURFACE.border,
                    background: active ? POPUP_SURFACE.primary : POPUP_SURFACE.subtle,
                  }}>
                  <OnboardSticker url={tw} glyph={s.emoji} />
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end" style={{ borderColor: POPUP_SURFACE.border, background: POPUP_SURFACE.subtle }}>
          <Button onClick={() => setOpen(false)}
            className="rounded-full text-white font-semibold px-5"
            style={{ background: POPUP_SURFACE.primary }} data-testid="sticker-browser-done">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Inline theme gallery: tier tabs + grid of every theme in that tier ─────
// ═══════════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════════
function SectionHeader({ title, subtitle }) {
  return (
    <header>
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'var(--st-font-heading)' }}>{title}</h2>
      <p className="text-sm mt-2 leading-relaxed" style={{ color: 'var(--st-mute)' }}>{subtitle}</p>
    </header>
  );
}

function FieldGroup({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-2.5">
        <label className="text-sm font-bold" style={{ fontFamily: 'var(--st-font-heading)' }}>{label}</label>
        {hint && <span className="text-[11px]" style={{ color: 'var(--st-mute)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function ChipList({ items, onRemove }) {
  if (!items?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      {items.map(v => (
        <span key={v} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-semibold"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          {v}
          <button onClick={() => onRemove(v)} className="opacity-50 hover:opacity-100">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

// "Picked just for you" — runs the rule-based recommender. Fits in 2 rows.
function RecommendedThemesRow({ signals, value, onPick }) {
  const picks = useMemo(() => recommendThemes(signals, 6), [
    signals.interests?.join('|'), signals.hobbies?.join('|'),
    signals.favorite_subjects?.join('|'), signals.custom_interests?.join('|'),
    signals.age_band, signals.learning_style,
  ]);
  if (!picks.length) return null;
  return (
    <div className="rounded-2xl border p-4" data-testid="onboard-recommend-row"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--st-mute)' }}>Recommended for you</div>
        <div className="text-[10px]" style={{ color: 'var(--st-mute)' }}>Based on your interests</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {picks.map(th => (
          <ThemePreviewCard
            key={th.id}
            th={th}
            active={value === th.id}
            onClick={() => onPick(th.id)}
            testid={`onboard-recommend-${th.id}`}
            compact
          />
        ))}
      </div>
    </div>
  );
}


// Fallback-aware sticker renderer for the onboarding sticker dialog.
function OnboardSticker({ url, glyph }) {
  const [failed, setFailed] = useState(false);
  if (failed || !url) return <span className="text-xl">{glyph}</span>;
  return <img src={url} alt="" className="w-7 h-7 object-contain" onError={() => setFailed(true)} />;
}
